/** Tiny SVG element helpers. DOM-only; no app state. */

export const SVG_NS = 'http://www.w3.org/2000/svg';

export type Attrs = Record<string, string | number | undefined | null | false>;

export function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (SVGElement | string)[] = [],
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  setAttrs(el, attrs);
  for (const child of children) {
    el.append(typeof child === 'string' ? child : child);
  }
  return el;
}

export function setAttrs(el: Element, attrs: Attrs): void {
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;
    el.setAttribute(key, String(value));
  }
}

/** `d` for an arc along a circle of `radius`, from `a0` to `a1` (clockwise, 12 o'clock = 0). */
export function arcPath(radius: number, a0: number, a1: number): string {
  const p0 = { x: radius * Math.sin(a0), y: -radius * Math.cos(a0) };
  const p1 = { x: radius * Math.sin(a1), y: -radius * Math.cos(a1) };
  const large = Math.abs(a1 - a0) > Math.PI ? 1 : 0;
  const sweep = a1 >= a0 ? 1 : 0;
  return `M ${n(p0.x)} ${n(p0.y)} A ${n(radius)} ${n(radius)} 0 ${large} ${sweep} ${n(p1.x)} ${n(p1.y)}`;
}

/** `d` for an annular sector between two radii, spanning `a0`..`a1`. */
export function annulusPath(rInner: number, rOuter: number, a0: number, a1: number): string {
  const large = Math.abs(a1 - a0) > Math.PI ? 1 : 0;
  const o0 = { x: rOuter * Math.sin(a0), y: -rOuter * Math.cos(a0) };
  const o1 = { x: rOuter * Math.sin(a1), y: -rOuter * Math.cos(a1) };
  const i1 = { x: rInner * Math.sin(a1), y: -rInner * Math.cos(a1) };
  const i0 = { x: rInner * Math.sin(a0), y: -rInner * Math.cos(a0) };
  return [
    `M ${n(o0.x)} ${n(o0.y)}`,
    `A ${n(rOuter)} ${n(rOuter)} 0 ${large} 1 ${n(o1.x)} ${n(o1.y)}`,
    `L ${n(i1.x)} ${n(i1.y)}`,
    `A ${n(rInner)} ${n(rInner)} 0 ${large} 0 ${n(i0.x)} ${n(i0.y)}`,
    'Z',
  ].join(' ');
}

/** Trim float noise out of path data. */
export function n(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/\.?0+$/, '');
}
